import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useSearchParams } from 'react-router';
import { GroupPolicyAction, useGroupPolicyCheck } from './groupPolicyCheck';
import * as styles from './GroupView.module.scss';
import GroupViewControls from './GroupViewControls';
import useDeleteGroup from './useDeleteGroup';
import useGroupMembersColumn from './useGroupMembersColumn';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { BannerEditorRef } from '../components/BannerEditor';
import BannerEditor from '../components/BannerEditor';
import type { Banner } from '../components/BannerModalContent';
import InPlaceEditable from '../components/InPlaceEditable';
import InPlaceEditControls from '../components/InPlaceEditControls';
import SideColumnLayout, {
  useIsCompactLayout,
} from '../components/SideColumnLayout';
import SummaryCollapsibleContent from '../components/SummaryCollapsibleContent';
import { DECLINE_ACCESS_TO, GRANT_ACCESS_TO } from '../constants/params';
import RichTextDescriptionEditor from '../editor/RichTextDescriptionEditor';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { GroupUserRole } from '../graphql/generated/graphql';
import MembershipTopics from '../topic/MembershipTopics';
import TopicIcon from '../topic/TopicIcon';
import { UIActions } from '../ui/uiSlice';
import handleApolloError from '../utils/error/handleApolloError';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';
import PageMeta from '../utils/PageMeta';
import { parseSlug } from '../utils/router/useSlug';
import useBeforeRouterLeaveConfirmWithModal from '../utils/useBeforeRouterLeaveConfirmWithModal';
import useIsEditingInPlace from '../utils/useIsEditingInPlace';
import useResettableState from '../utils/useResettableState';

const GROUP_VIEW_QUERY = gql(/** @complexity 26 */ `
  query GroupView($id: ID!) {
    group(id: $id) {
      id
      name
      description
      banner {
        original
      }
      requestors {
        id
      }
      membershipTopics {
        id
        ...MembershipTopics_topic
      }
      ...useGroupMembersColumn_group
      ...useGroupPolicyCheck_group
    }
  }
`);

const UPDATE_GROUP_MUTATION = gql(/** @complexity 4 */ `
  mutation GroupViewUpdateGroup(
    $id: ID!
    $banner: Upload
    $unsplashId: String
    $name: String
    $description: Json
  ) {
    updateGroup(
      id: $id
      banner: $banner
      unsplashId: $unsplashId
      name: $name
      description: $description
    ) {
      id
      name
      description
      banner {
        original
      }
    }
  }
`);

const ADD_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewAddUser($groupId: ID!, $userId: ID!) {
    user: addUserToGroup(groupId: $groupId, userId: $userId) {
      ...useGroupMembersColumn_member
    }
  }
`);

const UPDATE_USER_ROLE_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewUpdateUserRole(
    $groupId: ID!
    $userId: ID!
    $role: GroupUserRole!
  ) {
    user: updateUserInGroup(groupId: $groupId, userId: $userId, role: $role) {
      id
      name
    }
  }
`);

const REMOVE_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewRemoveUser($groupId: ID!, $userId: ID!) {
    user: removeUserFromGroup(groupId: $groupId, userId: $userId) {
      ...useGroupMembersColumn_member
    }
  }
`);

const REQUEST_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupViewRequestAccess($id: ID!) {
    requestGroupAccess(id: $id) {
      id
      requestors {
        id
      }
    }
  }
`);

const useMembershipEffect = (
  handleUpdateUserRole: (
    id: string,
    role: GroupUserRole,
  ) => Promise<{
    data?: DocumentType<typeof UPDATE_USER_ROLE_MUTATION> | null;
  }>,
  handleRemoveUser: (
    id: string,
  ) => Promise<{ data?: DocumentType<typeof REMOVE_USER_MUTATION> | null }>,
) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const grantAccessTo = searchParams.get(GRANT_ACCESS_TO);

    if (grantAccessTo) {
      setSearchParams({}, { replace: true });

      handleUpdateUserRole(grantAccessTo, GroupUserRole.Member)
        .then(({ data }) => {
          const user = data?.user;
          if (user) {
            dispatch(
              UIActions.pushToast({
                level: 'success',
                message: `${user.name} has been added as a member of this group.`,
              }),
            );
          }
        })
        .catch((error) => {
          handleApolloError(error, 'NOT_FOUND', () =>
            dispatch(
              UIActions.pushToast({
                level: 'success',
                message:
                  'Request has been rejected by another owner of this group.',
              }),
            ),
          );
        });
    }

    const declineAccessTo = searchParams.get(DECLINE_ACCESS_TO);

    if (declineAccessTo) {
      setSearchParams({}, { replace: true });

      handleRemoveUser(declineAccessTo).then(() =>
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: 'Membership request declined',
          }),
        ),
      );
    }
  }, [
    dispatch,
    searchParams,
    setSearchParams,
    handleUpdateUserRole,
    handleRemoveUser,
  ]);
};

type EditGroupPayload = Pick<
  DocumentType<typeof GROUP_VIEW_QUERY>['group'],
  'name' | 'description' | 'banner'
>;

type EditGroupState = Pick<EditGroupPayload, 'name' | 'description'> & {
  banner: Banner | null;
};

function getInitialState(group?: EditGroupPayload) {
  return {
    name: group?.name ?? '',
    description: group?.description ?? '',
    banner: group?.banner?.original ? { image: group.banner.original } : null,
  };
}

const GroupView = () => {
  const { groupSlug } = useParams();

  const [groupId] = parseSlug(groupSlug ?? '');
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const [isEditing, setIsEditing] = useIsEditingInPlace();

  const isCompactLayout = useIsCompactLayout();

  const showToast = (message: string) => {
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message,
      }),
    );
  };
  const { data, previousData, error } = useQuery(GROUP_VIEW_QUERY, {
    variables: { id: groupId },
  });

  useErrorRedirectEffect({
    error,
    noun: 'group',
  });

  const group = data?.group;

  const [
    editGroupState,
    { update: updateEditGroupState, reset: resetEditGroupState },
  ] = useResettableState<EditGroupState>(getInitialState(group));

  useEffect(() => {
    if (!isEditing || (data != null && previousData == null)) {
      resetEditGroupState(getInitialState(group));
    }
  }, [isEditing, group, resetEditGroupState, data, previousData]);

  const bannerEditor = useRef<BannerEditorRef>(null);
  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(true);

  const [updateGroup, { loading: isSaving }] = useMutation(
    UPDATE_GROUP_MUTATION,
  );

  const saveGroup = async () => {
    const bannerFile = await bannerEditor.current?.getBannerFile();
    const nextBanner =
      editGroupState.banner?.image !== currentBannerUrl
        ? bannerFile
        : undefined;

    const result = await updateGroup({
      variables: {
        id: groupId,
        name: editGroupState.name,
        description: JSON.stringify(editGroupState.description),
        banner: nextBanner,
        unsplashId: editGroupState.banner?.unsplashId,
      },
    });
    const resultGroup = result.data?.updateGroup;

    if (resultGroup) {
      // we need to use flush sync here because otherwise the state update will be batched
      // with the redirect happening via setIsEditing(false), but we need to make sure that
      // the state is resetted before the navigation happens so that the confirm modal from
      // useBeforeRouterLeaveConfirmWithModal will not show up
      flushSync(() => {
        resetEditGroupState(getInitialState(resultGroup));
      });
    }
  };

  useBeforeRouterLeaveConfirmWithModal({
    skip: !isEditing || !editGroupState.touched,
    onConfirm: saveGroup,
    title: 'Save changes?',
    message: "Please save your changes if you'd like to keep them.",
    confirmButtonText: 'Save',
    cancelButtonText: 'Discard',
  });

  const [addUserMutate] = useMutation(ADD_USER_MUTATION, {
    update: legacyUpdateQueryList(
      { query: GROUP_VIEW_QUERY, variables: { id: groupId } },
      'group.members',
      'user',
      true,
    ),
  });

  const handleAddUser = useCallback(
    (userId: string) => addUserMutate({ variables: { groupId, userId } }),
    [addUserMutate, groupId],
  );

  const [removeUserMutate] = useMutation(REMOVE_USER_MUTATION, {
    update: legacyUpdateQueryList(
      { query: GROUP_VIEW_QUERY, variables: { id: groupId } },
      ['group.members', 'group.owners', 'group.requestors'],
      'user',
      false,
    ),
  });
  const handleRemoveUser = useCallback(
    (userId: string) => removeUserMutate({ variables: { groupId, userId } }),
    [removeUserMutate, groupId],
  );

  const [updateUserRoleMutate] = useMutation(UPDATE_USER_ROLE_MUTATION, {
    refetchQueries: [{ query: GROUP_VIEW_QUERY, variables: { id: groupId } }],
  });
  const handleUpdateUserRole = useCallback(
    (userId: string, role: GroupUserRole) =>
      updateUserRoleMutate({ variables: { groupId, userId, role } }),
    [updateUserRoleMutate, groupId],
  );

  const [handleRequestAccess, { loading: isRequesting }] = useMutation(
    REQUEST_MUTATION,
    {
      variables: { id: groupId },
    },
  );

  const canEdit = useGroupPolicyCheck(GroupPolicyAction.UpdateGroup, group);
  const mainColumn = useGroupMembersColumn(
    group || undefined,
    handleAddUser,
    handleUpdateUserRole,
    handleRemoveUser,
  );
  const { id: userId } = useAppSelector((state) => state.session.user);

  useMembershipEffect(handleUpdateUserRole, handleRemoveUser);

  const onLeave = useGroupPolicyCheck(GroupPolicyAction.LeaveGroup, group)
    ? () =>
        handleRemoveUser(userId).then(() =>
          showToast('You have been removed from the group.'),
        )
    : undefined;
  const onJoin = useGroupPolicyCheck(GroupPolicyAction.JoinGroup, group)
    ? async () => {
        await handleAddUser(userId);
        await handleUpdateUserRole(userId, GroupUserRole.Owner);
        showToast('You were added as an owner.');
      }
    : undefined;

  const handleDelete = useDeleteGroup(groupId);

  const hasRequested = _.some(group?.requestors, { id: user.id });

  const onRequest = useGroupPolicyCheck(
    GroupPolicyAction.RequestMembership,
    group,
  )
    ? () =>
        handleRequestAccess().then(() =>
          showToast(
            'Your request has been sent to the group owner. You will receive an email after you’ve been accepted.',
          ),
        )
    : undefined;

  if (!group) {
    return null;
  }

  const { name, banner, description } = group;
  const currentBannerUrl = banner?.original;
  const isShowingBanner = currentBannerUrl != null || isEditing;

  const handleEditClick = () => setIsEditing(true);
  const handleSave = async () => {
    await saveGroup();
    setIsEditing(false);
  };

  const handleCancel = () => setIsEditing(false);

  const bannerEditorState = (() => {
    if (isSaving) return 'saving';
    return isEditing ? 'editing' : 'viewing';
  })();

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title={`${name} - Slab`} />
      {isShowingBanner && (
        <BannerEditor
          ref={bannerEditor}
          currentBannerUrl={currentBannerUrl}
          state={bannerEditorState}
          onBannerChange={(nextBanner) =>
            updateEditGroupState({ banner: nextBanner })
          }
          banner={editGroupState.banner}
        />
      )}
      <SideColumnLayout
        aboveAll={
          <div
            className={classNames(styles.aboveAllContainer, {
              [styles.isEditing]: isEditing,
            })}
          >
            <div className={styles.titleRow}>
              <h1>
                {isEditing ? (
                  <InPlaceEditable
                    defaultValue={group.name}
                    onChange={(nextName) =>
                      updateEditGroupState({ name: nextName })
                    }
                    placeholder="Enter a Group Name"
                    autoFocus
                  />
                ) : (
                  name
                )}
              </h1>

              <div className={styles.controlsContainer}>
                {isEditing ? (
                  <InPlaceEditControls
                    saving={isSaving}
                    savingDisabled={editGroupState.name === ''}
                    onCancelClick={handleCancel}
                    onSaveClick={handleSave}
                  />
                ) : (
                  <GroupViewControls
                    onLeave={onLeave}
                    onJoin={onJoin}
                    onRequest={onRequest}
                    requested={hasRequested}
                    isRequesting={isRequesting}
                    {...(canEdit
                      ? {
                          onEdit: handleEditClick,
                          onDelete: handleDelete,
                        }
                      : {})}
                  />
                )}
              </div>
            </div>
            {isEditing && (
              <div
                className={classNames(styles.description, {
                  [styles.isCompactLayout]: isCompactLayout,
                })}
              >
                <RichTextDescriptionEditor
                  readOnly={false}
                  noTitleLine
                  namespace="topicUserDescription"
                  contentPlaceholder="Write a description right here..."
                  defaultValue={description}
                  onTextChange={(nextDescription) =>
                    updateEditGroupState({ description: nextDescription })
                  }
                  testId="groupDescriptionEditor"
                />
              </div>
            )}
          </div>
        }
        aboveMain={
          !isEditing && (
            <SummaryCollapsibleContent
              collapsed={isDescriptionCollapsed}
              onCollapsedChange={setIsDescriptionCollapsed}
              maxLines={6}
              content={description}
              typography="postWithNoTitleLine"
            />
          )
        }
        main={mainColumn}
        sideColumn={{
          title: 'Topic membership',
          icon: <TopicIcon />,
          children: (
            <MembershipTopics
              interactionsDisabled={isEditing}
              membershipTopics={
                group?.membershipTopics?.map((topic) => ({
                  ...topic,
                  membershipChildTopics: [],
                })) ?? null
              }
            />
          ),
        }}
      />
    </PageLayout.Content>
  );
};

export default GroupView;
